import { useState, useMemo, useCallback } from 'react';
import { Bookmark } from '../types/bookmarks';
import { useBookmarkMutations } from './data/useBookmarkMutations';
import { useOnlineStatus } from './useOnlineStatus';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { copyToClipboard } from '../utils/clipboardService';
import {
  NOTES_PREVIEW_LENGTH,
  stripHtml,
  validateBookmarkName,
  computeNotesPreview,
} from '../utils/bookmarkUtils';

export { NOTES_PREVIEW_LENGTH, stripHtml, validateBookmarkName, computeNotesPreview };

export interface BookmarkCardState {
  // Edit state
  editName: string;
  editUrl: string;
  editNotes: string;
  nameError: string;
  confirmDeleteOpen: boolean;
  categoryPickerOpen: boolean;
  notesExpanded: boolean;
  loading: boolean;
  isOnline: boolean;

  // Computed
  plainTextNotes: string;
  truncatedNotes: string;
  notesExceedsPreview: boolean;
  disabledCategoryIds: Set<string>;
}

export interface BookmarkCardActions {
  setEditName: (name: string) => void;
  setEditUrl: (url: string) => void;
  setEditNotes: (notes: string) => void;
  setNotesExpanded: (expanded: boolean) => void;
  handleEditStart: () => void;
  handleSave: () => Promise<void>;
  handleDeleteClick: () => void;
  handleConfirmDelete: () => Promise<void>;
  handleCancelDelete: () => void;
  handleMoveClick: () => void;
  handleMoveSelect: (categoryId: string | null) => Promise<void>;
  handleMoveCancel: () => void;
  handleCopyUrl: () => Promise<void>;
  handleCopyNotes: () => Promise<void>;
}

interface UseBookmarkCardOptions {
  bookmark: Bookmark;
  isNew?: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onDelete?: () => void;
}

export function useBookmarksModel({
  bookmark,
  isNew,
  onEditStart,
  onEditEnd,
  onDelete,
}: UseBookmarkCardOptions): BookmarkCardState & BookmarkCardActions {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [editName, setEditName] = useState(bookmark.bookmark_name);
  const [editUrl, setEditUrl] = useState(bookmark.bookmark_url);
  const [editNotes, setEditNotes] = useState(bookmark.bookmark_notes);
  const [nameError, setNameError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);

  const { addBookmark, updateBookmark, deleteBookmark, moveBookmark, loading } =
    useBookmarkMutations();
  const { isOnline } = useOnlineStatus();
  const { setSnackbar } = useWebAuthn();

  const { plainText: plainTextNotes, truncated: truncatedNotes, exceedsPreview: notesExceedsPreview } =
    useMemo(() => computeNotesPreview(bookmark.bookmark_notes), [bookmark.bookmark_notes]);

  const disabledCategoryIds = useMemo(
    () => new Set<string>(bookmark.category_id ? [bookmark.category_id] : []),
    [bookmark.category_id]
  );

  const handleEditStart = useCallback(() => {
    setEditName(bookmark.bookmark_name);
    setEditUrl(bookmark.bookmark_url);
    setEditNotes(bookmark.bookmark_notes);
    setNameError('');
    onEditStart();
  }, [bookmark, onEditStart]);

  const handleSave = useCallback(async () => {
    const { valid, error } = validateBookmarkName(editName);
    if (!valid) {
      setNameError(error);
      return;
    }
    setNameError('');

    if (isNew) {
      const result = await addBookmark({
        bookmark_name: editName.trim(),
        bookmark_url: editUrl,
        bookmark_notes: editNotes,
        category_id: bookmark.category_id || null,
      });
      if (result.success) {
        onEditEnd();
      } else {
        setSnackbar({
          open: true,
          message: 'Could not save bookmark. Please try again.',
          severity: 'error',
        });
      }
    } else {
      const result = await updateBookmark(bookmark.bookmark_id, {
        bookmark_name: editName.trim(),
        bookmark_url: editUrl,
        bookmark_notes: editNotes,
      });
      if (result.success) {
        onEditEnd();
      } else {
        setSnackbar({
          open: true,
          message: 'Could not save bookmark. Please try again.',
          severity: 'error',
        });
      }
    }
  }, [editName, editUrl, editNotes, isNew, bookmark, addBookmark, updateBookmark, onEditEnd, setSnackbar]);

  const handleDeleteClick = useCallback(() => {
    if (isNew) {
      if (onDelete) {
        onDelete();
      } else {
        onEditEnd();
      }
    } else {
      setConfirmDeleteOpen(true);
    }
  }, [isNew, onDelete, onEditEnd]);

  const handleConfirmDelete = useCallback(async () => {
    setConfirmDeleteOpen(false);
    const result = await deleteBookmark(bookmark.bookmark_id);
    if (result.success) {
      if (onDelete) {
        onDelete();
      } else {
        onEditEnd();
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Could not delete bookmark. Please try again.',
        severity: 'error',
      });
    }
  }, [bookmark.bookmark_id, deleteBookmark, onDelete, onEditEnd, setSnackbar]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const handleMoveClick = useCallback(() => {
    setCategoryPickerOpen(true);
  }, []);

  const handleMoveSelect = useCallback(async (categoryId: string | null) => {
    setCategoryPickerOpen(false);
    const result = await moveBookmark(bookmark.bookmark_id, categoryId);
    if (result.success) {
      if (onDelete) {
        onDelete();
      } else {
        onEditEnd();
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Could not move bookmark. Please try again.',
        severity: 'error',
      });
    }
  }, [bookmark.bookmark_id, moveBookmark, onDelete, onEditEnd, setSnackbar]);

  const handleMoveCancel = useCallback(() => {
    setCategoryPickerOpen(false);
  }, []);

  const handleCopyUrl = useCallback(async () => {
    const success = await copyToClipboard(bookmark.bookmark_url);
    if (success) {
      setSnackbar({ open: true, message: 'URL copied to clipboard', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Copy failed', severity: 'error' });
    }
  }, [bookmark.bookmark_url, setSnackbar]);

  const handleCopyNotes = useCallback(async () => {
    const success = await copyToClipboard(plainTextNotes);
    if (success) {
      setSnackbar({ open: true, message: 'Notes copied to clipboard', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Copy failed', severity: 'error' });
    }
  }, [plainTextNotes, setSnackbar]);

  return {
    // State
    editName,
    editUrl,
    editNotes,
    nameError,
    confirmDeleteOpen,
    categoryPickerOpen,
    notesExpanded,
    loading,
    isOnline,
    plainTextNotes,
    truncatedNotes,
    notesExceedsPreview,
    disabledCategoryIds,
    // Actions
    setEditName: (name: string) => {
      setEditName(name);
      if (nameError) setNameError('');
    },
    setEditUrl,
    setEditNotes,
    setNotesExpanded,
    handleEditStart,
    handleSave,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleMoveClick,
    handleMoveSelect,
    handleMoveCancel,
    handleCopyUrl,
    handleCopyNotes,
  };
}
